import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Play, Pause, X } from "lucide-react";
import WaveSurfer from "wavesurfer.js";
import RegionsPlugin from "wavesurfer.js/dist/plugins/regions.esm.js";
import { adm, apiErr } from "../../lib/adminApi";
import { Btn, TInput } from "./ui";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const QUICK = [15, 30, 45, 60];
const fmt = (s) => `${Math.floor((s || 0) / 60)}:${String(Math.floor((s || 0) % 60)).padStart(2, "0")}`;

export default function TeaserStudio({ releaseId, track, onGenerated, onClose }) {
  const containerRef = useRef(null);
  const wsRef = useRef(null);
  const regionRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [dur, setDur] = useState(0);
  const [sel, setSel] = useState({ start: 0, end: 15 });
  const [fadeIn, setFadeIn] = useState(0);
  const [fadeOut, setFadeOut] = useState(0);
  const [previewing, setPreviewing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    const regions = RegionsPlugin.create();
    const ws = WaveSurfer.create({
      container: containerRef.current,
      url: `${API}/admin/media/${track.fullMediaId}/audio`,
      fetchParams: { credentials: "include" },
      waveColor: "#4a4438",
      progressColor: "#d4af37",
      cursorColor: "#f5f5f0",
      height: 110,
      normalize: true,
      plugins: [regions],
    });
    wsRef.current = ws;
    ws.on("ready", () => {
      const d = ws.getDuration();
      setDur(d);
      const g = track.generatedTeaser;
      const start = g?.start != null && g.start < d ? g.start : 0;
      const end = g?.end != null && g.end <= d ? g.end : Math.min(start + 15, d);
      const region = regions.addRegion({
        start, end, drag: true, resize: true,
        color: "rgba(212,175,55,0.22)",
      });
      regionRef.current = region;
      setSel({ start: region.start, end: region.end });
      setReady(true);
    });
    ws.on("error", () => toast.error("Could not load the master audio."));
    regions.on("region-updated", (r) => setSel({ start: r.start, end: r.end }));
    regions.on("region-out", () => { ws.pause(); setPreviewing(false); });
    ws.on("pause", () => setPreviewing(false));
    return () => ws.destroy();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [track.fullMediaId]);

  const setQuick = (d) => {
    const r = regionRef.current;
    if (!r || !dur) return;
    const start = Math.min(r.start, Math.max(0, dur - d));
    r.setOptions({ start, end: Math.min(start + d, dur) });
    setSel({ start, end: Math.min(start + d, dur) });
  };

  const previewSelection = () => {
    const ws = wsRef.current, r = regionRef.current;
    if (!ws || !r) return;
    if (previewing) { ws.pause(); return; }
    ws.setTime(r.start);
    ws.play();
    setPreviewing(true);
  };

  const generate = async () => {
    setBusy(true);
    try {
      const res = await adm.generateTeaser({
        releaseId, trackId: track.id,
        start: Number(sel.start.toFixed(2)), end: Number(sel.end.toFixed(2)),
        fadeIn: Number(fadeIn) || 0, fadeOut: Number(fadeOut) || 0,
      });
      setResult(res);
      onGenerated(res);
      toast.success("Teaser generated — the master was not modified.");
    } catch (e) { toast.error(apiErr(e)); }
    finally { setBusy(false); }
  };

  const selDur = Math.max(0, sel.end - sel.start);

  return (
    <div className="fixed inset-0 z-[110] bg-black/85 flex items-center justify-center p-4" data-testid="teaser-studio">
      <div className="bg-[#0a0a0a] border border-white/15 w-full max-w-4xl max-h-[92vh] overflow-y-auto p-6 md:p-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <p className="font-mono text-[10px] tracking-[0.35em] text-gold">TEASER STUDIO — NON-DESTRUCTIVE</p>
            <p className="font-serif text-2xl text-white mt-2">{track.title}</p>
          </div>
          <button onClick={onClose} data-testid="teaser-studio-close" aria-label="Close studio"
            className="text-white/50 hover:text-white"><X size={20} /></button>
        </div>

        <p className="font-mono text-[9px] tracking-[0.2em] text-white/35 mb-3">
          DRAG THE GOLD REGION TO THE PART VISITORS SHOULD HEAR. THE MASTER IS NEVER MODIFIED OR EXPOSED.
        </p>
        <div ref={containerRef} className="border border-white/10 bg-[#060606] p-3" data-testid="teaser-waveform" />
        {!ready && <p className="font-mono text-[10px] text-white/30 mt-3">DECODING MASTER WAVEFORM…</p>}

        {ready && (
          <>
            <div className="flex flex-wrap items-center gap-2.5 mt-5">
              {QUICK.map((d) => (
                <button key={d} onClick={() => setQuick(d)} disabled={dur < 3} data-testid={`teaser-quick-${d}`}
                  className={`font-mono text-[10px] tracking-[0.2em] border px-4 py-2 transition-colors ${
                    Math.round(selDur) === d ? "border-gold text-gold" : "border-white/15 text-white/50 hover:border-white/40"}`}>
                  {d}″
                </button>
              ))}
              <span className="font-mono text-[10px] tracking-[0.2em] border border-white/15 text-white/50 px-4 py-2">CUSTOM: DRAG EDGES</span>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mt-5">
              <div><p className="font-mono text-[8px] tracking-[0.25em] text-white/40">START</p>
                <p className="font-mono text-lg text-white mt-1" data-testid="teaser-start">{fmt(sel.start)}</p></div>
              <div><p className="font-mono text-[8px] tracking-[0.25em] text-white/40">END</p>
                <p className="font-mono text-lg text-white mt-1" data-testid="teaser-end">{fmt(sel.end)}</p></div>
              <div><p className="font-mono text-[8px] tracking-[0.25em] text-white/40">SELECTED</p>
                <p className="font-mono text-lg text-gold mt-1" data-testid="teaser-duration">{selDur.toFixed(1)}″</p></div>
              <TInput label="FADE IN (S)" value={fadeIn} onChange={setFadeIn} type="number" testid="teaser-fadein" />
              <TInput label="FADE OUT (S)" value={fadeOut} onChange={setFadeOut} type="number" testid="teaser-fadeout" />
            </div>

            <div className="flex flex-wrap items-center gap-4 mt-7">
              <Btn variant="ghost" onClick={previewSelection} testid="teaser-preview-btn">
                <span className="inline-flex items-center gap-2">{previewing ? <Pause size={12} /> : <Play size={12} />} {previewing ? "STOP PREVIEW" : "PREVIEW SELECTION"}</span>
              </Btn>
              <Btn onClick={generate} disabled={busy || selDur < 3} testid="teaser-generate-btn">
                {busy ? "RENDERING…" : track.generatedTeaser ? "REGENERATE TEASER" : "GENERATE TEASER"}
              </Btn>
              <Btn variant="ghost" onClick={onClose} testid="teaser-save-btn">SAVE &amp; CLOSE</Btn>
            </div>

            {(result || track.generatedTeaser?.mediaId) && (
              <div className="mt-7 border-t border-white/10 pt-5" data-testid="teaser-result">
                <p className="font-mono text-[9px] tracking-[0.3em] text-gold mb-3">
                  GENERATED TEASER DERIVATIVE {result ? "(NEW)" : "(CURRENT)"} — THIS IS THE ONLY FILE THE PUBLIC RECEIVES
                </p>
                <audio controls preload="metadata" className="w-full"
                  src={`${API}/media/public/${result?.generatedTeaser?.mediaId || track.generatedTeaser.mediaId}`}
                  data-testid="teaser-result-audio" />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
